import { useState, useEffect } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const CMPStep3SitePlanning = ({ project, onComplete, onBack }) => {
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [siteEstablishmentImage, setSiteEstablishmentImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  
  // Site plan checklist
  const [sitePlanItems, setSitePlanItems] = useState([
    'site_boundary', 'hoarding_fencing', 'vehicle_access', 
    'pedestrian_interface', 'sediment_controls', 'waste_storage'
  ]);
  
  // Hoarding classes
  const [hoardingClasses, setHoardingClasses] = useState(['class_a']);
  
  // Tree protection
  const [hasTreeProtectionPlan, setHasTreeProtectionPlan] = useState(false);

  useEffect(() => {
    // Load existing data
    if (project?.cmp_data) {
      if (project.cmp_data.site_plan_items) setSitePlanItems(project.cmp_data.site_plan_items);
      if (project.cmp_data.hoarding_classes) setHoardingClasses(project.cmp_data.hoarding_classes);
      if (project.cmp_data.has_tree_protection_plan !== undefined) {
        setHasTreeProtectionPlan(project.cmp_data.has_tree_protection_plan);
      }
      // Handle site establishment image
      if (project.cmp_data.site_establishment_image) {
        const imgRef = project.cmp_data.site_establishment_image;
        if (imgRef.file_data) {
          // Old format - base64 stored in document
          setImagePreview(`data:image/png;base64,${imgRef.file_data}`);
        } else if (imgRef.file_id) {
          // New format - fetch from server
          const token = localStorage.getItem('opwmp_token');
          axios.get(`${API}/projects/${project.id}/cmp/site-establishment-image`, {
            headers: { Authorization: `Bearer ${token}` },
            responseType: 'blob'
          }).then(response => {
            const reader = new FileReader();
            reader.onloadend = () => setImagePreview(reader.result);
            reader.readAsDataURL(response.data);
          }).catch(err => {
            console.error('Error loading site establishment image:', err);
          });
        }
      }
    }
  }, [project]);

  const toggleSitePlanItem = (item) => {
    setSitePlanItems(prev => 
      prev.includes(item) ? prev.filter(i => i !== item) : [...prev, item]
    );
  };

  const toggleHoardingClass = (cls) => {
    setHoardingClasses(prev => 
      prev.includes(cls) ? prev.filter(c => c !== cls) : [...prev, cls]
    );
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Preview
    const reader = new FileReader();
    reader.onloadend = () => setImagePreview(reader.result);
    reader.readAsDataURL(file);
    
    setSiteEstablishmentImage(file);

    // Upload immediately
    setUploading(true);
    try {
      const token = localStorage.getItem('opwmp_token');
      const formData = new FormData();
      formData.append('file', file);

      await axios.post(`${API}/projects/${project.id}/cmp/site-establishment-image`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Failed to upload image. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const token = localStorage.getItem('opwmp_token');
      
      await axios.post(`${API}/projects/${project.id}/cmp/step-data`, {
        site_plan_items: sitePlanItems,
        hoarding_classes: hoardingClasses,
        has_tree_protection_plan: hasTreeProtectionPlan
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      await axios.put(`${API}/projects/${project.id}/step`, {
        step_number: 4
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      onComplete({ ...project, current_step: 4 });
    } catch (error) {
      console.error('Error saving site planning:', error);
      alert('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const sitePlanOptions = [
    { key: 'site_boundary', label: 'Site boundary' },
    { key: 'hoarding_fencing', label: 'Hoarding / fencing' },
    { key: 'vehicle_access', label: 'Vehicle access points' },
    { key: 'pedestrian_interface', label: 'Pedestrian interface' },
    { key: 'sediment_controls', label: 'Sediment controls' },
    { key: 'waste_storage', label: 'Waste storage area' }
  ];

  const hoardingOptions = [
    { key: 'class_a', label: 'Class A - Light Duty (internally contained)' },
    { key: 'class_b', label: 'Class B - Medium Duty (moderate wind loads)' },
    { key: 'class_c', label: 'Class C - Heavy Duty (high-risk areas)' }
  ];

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
        {/* Back button at top */}
        <div className="flex items-center justify-between mb-4">
          <button
            type="button"
            onClick={onBack}
            className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Site Planning and Layout</h2>
        <p className="text-gray-600 mb-8">
          Upload your site establishment plan and configure site management details.
        </p>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Site Establishment Plan Upload */}
          <div className="border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              4.0 Site Establishment Plan
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Upload a site establishment plan showing site boundaries, access arrangements, 
              hoarding, environmental controls, and key site facilities.
            </p>
            
            <div className="mt-4">
              <label className="block">
                <span className="sr-only">Choose site plan image</span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                />
              </label>
              {uploading && <p className="text-sm text-blue-600 mt-2">Uploading...</p>}
              {imagePreview && (
                <div className="mt-4">
                  <img src={imagePreview} alt="Site Establishment Plan" className="max-w-full h-auto rounded-lg border" />
                </div>
              )}
            </div>

            {/* Site Plan Checklist */}
            <div className="mt-6">
              <h4 className="text-sm font-semibold text-gray-800 mb-3">Site Plan Elements</h4>
              <div className="grid grid-cols-2 gap-3">
                {sitePlanOptions.map(option => (
                  <label key={option.key} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={sitePlanItems.includes(option.key)}
                      onChange={() => toggleSitePlanItem(option.key)}
                      className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">{option.label}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Hoarding Classes */}
          <div className="border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              4.4 Hoarding, Fencing and Public Protection
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Select the classes of hoarding that will be utilised on this project:
            </p>
            <div className="space-y-3">
              {hoardingOptions.map(option => (
                <label key={option.key} className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={hoardingClasses.includes(option.key)}
                    onChange={() => toggleHoardingClass(option.key)}
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">{option.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Tree Protection */}
          <div className="border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              4.5 Tree Protection Measures
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Does the development include a Tree Protection Plan?
            </p>
            <div className="flex gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="treeProtection"
                  checked={hasTreeProtectionPlan}
                  onChange={() => setHasTreeProtectionPlan(true)}
                  className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">Yes</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="treeProtection"
                  checked={!hasTreeProtectionPlan}
                  onChange={() => setHasTreeProtectionPlan(false)}
                  className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">No</span>
              </label>
            </div>
            {hasTreeProtectionPlan && (
              <p className="mt-3 text-sm text-green-700 bg-green-50 p-3 rounded-lg">
                A Tree Protection Management Plan (TPMP) prepared in accordance with AS 4970:2009 
                is to be included as part of the development application.
              </p>
            )}
          </div>

          {/* Navigation */}
          <div className="flex justify-between pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onBack}
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              Back
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Continue'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CMPStep3SitePlanning;
